import { motion } from 'framer-motion'
import {
  Activity,
  ArrowUpRight,
  CalendarPlus,
  Crown,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { ProgressRing } from '@/components/dashboard/ProgressRing'
import { useCommandDashboard } from '@/contexts/CommandDashboardContext'
import { dashboardBusinessDate, parseDashboardMoney } from '@/lib/dashboard/commandMetrics'
import { useCountUp } from '@/hooks/useCountUp'
import { cn } from '@/lib/utils'

export function HeroHeader() {
  const navigate = useNavigate()
  const { data } = useCommandDashboard()
  const meta = data?.meta ?? {}
  const hero = data?.hero ?? {}
  const pulse = hero.salon_pulse ?? {}
  const kpis = Array.isArray(data?.kpis) ? data.kpis : []

  const target = parseDashboardMoney(hero.daily_target)
  const revenue = parseDashboardMoney(hero.revenue_today)
  const changePct = Number(hero.revenue_change_pct ?? 0)
  const progress = Number(
    hero.target_progress_pct ?? (target > 0 ? Math.min(100, Math.round((revenue / target) * 100)) : 0),
  )
  const remaining = parseDashboardMoney(hero.amount_remaining ?? Math.max(0, target - revenue))

  const hour = new Date().getHours()
  const greeting =
    hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const revenueDisplay = useCountUp(revenue, 800)
  const dateLabel = new Intl.DateTimeFormat('en-AE', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  }).format(dashboardBusinessDate(meta))

  const apptKpi = kpis.find((k) => k.title === 'Appointments')
  const utilKpi = kpis.find((k) => k.title === 'Utilization')
  const vipKpi = kpis.find((k) => k.title === 'VIP guests')

  const ownerStats = [
    {
      icon: TrendingUp,
      label: 'Revenue',
      value: `AED ${revenueDisplay.toLocaleString()}`,
      sub: `${progress}% of target`,
      accent: 'from-primary/15 to-primary/5 text-primary',
    },
    {
      icon: Sparkles,
      label: 'Appointments',
      value: apptKpi?.value ?? '0',
      sub: apptKpi?.context ?? 'Today',
      accent: 'from-violet-500/15 to-violet-500/5 text-violet-700',
    },
    {
      icon: Activity,
      label: 'Occupancy',
      value: utilKpi?.value ?? `${pulse.occupancy_pct ?? 0}%`,
      sub: utilKpi?.context ?? `${pulse.guests_inside ?? 0} guests inside`,
      accent: 'from-emerald-500/15 to-emerald-500/5 text-emerald-700',
    },
    {
      icon: Crown,
      label: 'VIP today',
      value: vipKpi?.value ?? '0',
      sub: vipKpi?.context ?? 'Bridal / VIP',
      accent: 'from-amber-500/15 to-amber-500/5 text-amber-800',
    },
  ]

  const pulseItems = [
    { icon: Users, label: `${pulse.guests_inside ?? 0} guests inside`, highlight: true },
    {
      icon: Activity,
      label: `${pulse.therapists_active ?? 0} / ${pulse.therapists_total ?? 0} therapists active`,
    },
    { icon: TrendingUp, label: `${pulse.occupancy_pct ?? 0}% floor occupancy` },
    {
      icon: ArrowUpRight,
      label: pulse.pace_label ?? 'Pace: on track',
      accent: 'text-emerald-600',
    },
    { icon: Target, label: `Goal ${pulse.goal_pct ?? progress}% complete` },
  ]

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-hero relative overflow-hidden p-5 lg:p-7"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-gold/12 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 80%, hsl(262 83% 58% / 0.08), transparent 45%), radial-gradient(circle at 80% 20%, hsl(31 53% 64% / 0.12), transparent 40%)',
        }}
      />

      <div className="relative space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold tracking-wide text-primary">
              {meta.branch_name ?? 'Branch'} · Owner overview
            </p>
            <h1 className="mt-1.5 text-2xl font-bold tracking-tight sm:text-3xl lg:text-[2.125rem]">
              {greeting},{' '}
              <span className="text-gradient-luxury">{meta.display_name ?? 'Team'}</span>
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{dateLabel}</p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-9 rounded-xl text-xs shadow-md shadow-primary/20"
              type="button"
              onClick={() => navigate('/calendar?book=1')}
            >
              <CalendarPlus className="h-3.5 w-3.5" />
              Book
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 rounded-xl border-primary/15 bg-white/60 text-xs shadow-sm backdrop-blur hover:bg-white/90"
              type="button"
              onClick={() => navigate('/pos')}
            >
              <Wallet className="h-3.5 w-3.5" />
              POS
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4 lg:gap-3">
          {ownerStats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + i * 0.04 }}
              className={cn(
                'rounded-2xl bg-gradient-to-br p-3.5 shadow-sm backdrop-blur-sm',
                stat.accent,
              )}
              style={{ border: '1px solid hsl(262 20% 50% / 0.06)' }}
            >
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider opacity-80">
                <stat.icon className="h-3.5 w-3.5" strokeWidth={2.25} />
                {stat.label}
              </div>
              <p className="mt-1.5 text-lg font-bold tabular-nums leading-none tracking-tight sm:text-xl">
                {stat.value}
              </p>
              <p className="mt-1 text-[10px] font-medium opacity-75">{stat.sub}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_240px] lg:items-end">
          <div className="rounded-2xl bg-white/50 p-4 shadow-sm backdrop-blur-sm lg:p-5">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Revenue today
                </p>
                <div className="mt-1 flex flex-wrap items-baseline gap-2.5">
                  <p className="text-3xl font-bold tabular-nums leading-none tracking-tight sm:text-4xl">
                    AED {revenueDisplay.toLocaleString()}
                  </p>
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-1 text-[11px] font-bold',
                      changePct >= 0
                        ? 'bg-emerald-500/12 text-emerald-700'
                        : 'bg-rose-500/12 text-rose-700',
                    )}
                  >
                    {changePct >= 0 ? '▲' : '▼'} {Math.abs(changePct)}% vs yesterday
                  </span>
                </div>
                <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-muted/80 shadow-inner">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.9, ease: 'easeOut' }}
                    className="h-full rounded-full bg-gradient-to-r from-primary via-secondary to-gold"
                  />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Target{' '}
                  <span className="font-semibold text-foreground">AED {target.toLocaleString()}</span>
                  {' · '}
                  <span className="font-semibold text-primary">{progress}%</span>
                  {' · '}
                  AED {remaining.toLocaleString()} to go
                </p>
              </div>
              <ProgressRing value={progress} size={72} stroke={5}>
                <span className="text-sm font-bold text-primary">{progress}%</span>
              </ProgressRing>
            </div>
          </div>

          <div className="rounded-2xl bg-white/55 p-4 shadow-luxury-sm backdrop-blur">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold tracking-tight">Live pulse</h2>
              <span className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-live" />
                Live
              </span>
            </div>
            <ul className="mt-3 space-y-2">
              {pulseItems.map((item) => (
                <li key={item.label} className="flex items-center gap-2 text-[12px]">
                  <item.icon
                    className={cn(
                      'h-3.5 w-3.5 shrink-0',
                      item.highlight ? 'text-primary' : 'text-muted-foreground',
                    )}
                    strokeWidth={2}
                  />
                  <span className={cn('leading-snug', item.accent ?? 'text-foreground/90')}>
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.section>
  )
}
